// Session status write. The review cohort supplies the team-member fallback;
// the assistant-view sentinel is never a member id. Analytics crosses as
// `onStatusUpdated`. The error copy is static meta.
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useAtomValue } from 'jotai';

import { logger, sanitizeError } from '@heidi/runtime/logger';
import { type SessionStatus } from '@heidi/schema/ml-scribe';

import {
  ASSISTANT_VIEW_SELECT_ID,
  GET_INFINITE_SESSIONS,
  GET_INFINITE_SESSIONS_WITH_PAGINATION,
  GET_SESSION_BY_ID,
  GET_SESSIONS,
  GET_SESSIONS_LIST,
  GET_TEAM_MEMBER_SESSION_BY_ID,
  type SessionListResponse,
  type SessionResponse,
  type SessionsResponse,
} from '../../contracts';
import { updateSessionById, updateSessionForOtherUser } from '../api/session';
import { sessionQueryVersionAtom } from '../atoms/sessionAtoms';
import { invalidateSessionStatusCount } from '../query/sessionStatusCountKeys';
import {
  invalidateSessionsQuery,
  invalidateTeamMemberSessionsQuery,
} from '../query/sessionsListCache';
import { invalidateSessionById } from '../query/useQuerySessionById';
import { useSessionReviewCohort } from '../sessionReviewCohort';

type UpdateSessionStatusInput = {
  sessionId: string;
  sessionStatus: SessionStatus;
  /** Team-member endpoint ids. Omitted ids fall back to the review cohort. */
  teamId?: string;
  memberId?: string;
};

const STATUS_ERROR_META = {
  errorToast: { title: 'Failed to update session status' },
} as const;

type StatusUpdated = {
  sessionId: string;
  sessionStatus: SessionStatus;
};

export function useMutateUpdateSessionStatus({
  onStatusUpdated,
}: {
  onStatusUpdated?: (input: StatusUpdated) => void;
} = {}) {
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const queryClient = useQueryClient();
  const { isReviewingOtherUser, teamId, memberId } = useSessionReviewCohort();
  const fallbackTeamMemberContext =
    isReviewingOtherUser && teamId && memberId && memberId !== ASSISTANT_VIEW_SELECT_ID
      ? { teamId, memberId }
      : null;

  const resolveTeamMemberContext = ({
    teamId: inputTeamId,
    memberId: inputMemberId,
  }: Pick<UpdateSessionStatusInput, 'teamId' | 'memberId'>) => {
    if (inputTeamId && inputMemberId) {
      return { teamId: inputTeamId, memberId: inputMemberId };
    }
    return fallbackTeamMemberContext;
  };

  return useMutation({
    meta: STATUS_ERROR_META,
    mutationFn: async ({
      sessionId,
      sessionStatus,
      teamId: inputTeamId,
      memberId: inputMemberId,
    }: UpdateSessionStatusInput) => {
      const teamMemberContext = resolveTeamMemberContext({
        teamId: inputTeamId,
        memberId: inputMemberId,
      });
      if (teamMemberContext) {
        return await updateSessionForOtherUser({
          teamId: teamMemberContext.teamId,
          memberId: teamMemberContext.memberId,
          sessionId,
          input: {
            session_status: sessionStatus,
          },
        });
      }
      return await updateSessionById(sessionId, {
        session_status: sessionStatus,
      });
    },

    onMutate: ({
      sessionId,
      sessionStatus,
      teamId: inputTeamId,
      memberId: inputMemberId,
    }) => {
      const teamMemberContext = resolveTeamMemberContext({
        teamId: inputTeamId,
        memberId: inputMemberId,
      });

      void queryClient.setQueriesData<InfiniteData<SessionListResponse>>(
        { queryKey: [GET_SESSIONS_LIST] },
        (oldData) => {
          if (!oldData) return undefined;
          return produce(oldData, (draft) => {
            const session = draft.pages
              .flatMap((page) => page.sessions)
              .find((s) => s.session_id === sessionId);
            if (session) {
              session.session_status = sessionStatus;
            }
          });
        }
      );

      switch (sessionQueryVersion) {
        case 'v3':
        case 'v2':
          void queryClient.setQueriesData<InfiniteData<SessionsResponse>>(
            {
              queryKey: [
                sessionQueryVersion === 'v3'
                  ? GET_INFINITE_SESSIONS_WITH_PAGINATION
                  : GET_INFINITE_SESSIONS,
              ],
            },
            (oldData: InfiniteData<SessionsResponse> | undefined) => {
              if (!oldData) {
                return undefined;
              }
              return produce(oldData, (draft) => {
                const session = draft.pages
                  .flatMap((page) => page.sessions)
                  .find((session) => session.session_id === sessionId);
                if (session) {
                  session.session_status = sessionStatus;
                }
              });
            }
          );
          break;
        default:
          void queryClient.setQueryData<SessionsResponse>([GET_SESSIONS], (oldData) => {
            if (!oldData) {
              return undefined;
            }
            return produce(oldData, (draft) => {
              const session = draft.sessions.find(
                (session) => session.session_id === sessionId
              );
              if (session) {
                session.session_status = sessionStatus;
              }
            });
          });
      }

      const teamMemberSessionQueryKey = teamMemberContext
        ? [
            GET_TEAM_MEMBER_SESSION_BY_ID,
            { teamId: teamMemberContext.teamId },
            { memberId: teamMemberContext.memberId },
            { sessionId },
          ]
        : null;

      const applyStatusPatch = (oldData: SessionResponse | undefined) => {
        if (!oldData) return undefined;
        return produce(oldData, (draft) => {
          draft.session_status = sessionStatus;
        });
      };

      void queryClient.setQueryData<SessionResponse>(
        [GET_SESSION_BY_ID, { sessionId }],
        applyStatusPatch
      );

      if (teamMemberSessionQueryKey) {
        void queryClient.setQueryData<SessionResponse>(
          teamMemberSessionQueryKey,
          applyStatusPatch
        );
      }
    },

    onSuccess: (
      _,
      { sessionId, sessionStatus, teamId: inputTeamId, memberId: inputMemberId }
    ) => {
      const teamMemberContext = resolveTeamMemberContext({
        teamId: inputTeamId,
        memberId: inputMemberId,
      });
      if (teamMemberContext) {
        void invalidateTeamMemberSessionsQuery(
          teamMemberContext.teamId,
          teamMemberContext.memberId
        );
        invalidateSessionById(sessionId);
      }
      void invalidateSessionStatusCount();
      onStatusUpdated?.({ sessionId, sessionStatus });
    },

    onError: (error, { sessionId, teamId: inputTeamId, memberId: inputMemberId }) => {
      const teamMemberContext = resolveTeamMemberContext({
        teamId: inputTeamId,
        memberId: inputMemberId,
      });
      if (teamMemberContext) {
        void invalidateTeamMemberSessionsQuery(
          teamMemberContext.teamId,
          teamMemberContext.memberId
        );
        void queryClient.invalidateQueries({
          queryKey: [
            GET_TEAM_MEMBER_SESSION_BY_ID,
            { teamId: teamMemberContext.teamId },
            { memberId: teamMemberContext.memberId },
            sessionId,
          ],
        });
        void queryClient.invalidateQueries({ queryKey: [GET_SESSIONS_LIST] });
        invalidateSessionById(sessionId);
      } else {
        void invalidateSessionsQuery();
        invalidateSessionById(sessionId);
      }
      logger.error('Failed to update session status', { error: sanitizeError(error) });
    },
  });
}
